import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

// Custom Alert Dialog
export const useAlert = () => {
  const [alertState, setAlertState] = useState({ isOpen: false, message: '', type: 'info' });

  const showAlert = (message, type = 'info') => {
    setAlertState({ isOpen: true, message, type });
  };

  const AlertDialog = () => (
    <AnimatePresence>
      {alertState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setAlertState({ ...alertState, isOpen: false })}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md"
          >
            <div 
              className="glass-card rounded-xl p-6"
              style={{
                background: 'rgba(10, 10, 11, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  alertState.type === 'error' ? 'bg-[#FF2E2E]/20' :
                  alertState.type === 'success' ? 'bg-[#00FF94]/20' :
                  'bg-[#00F0FF]/20'
                }`}>
                  {alertState.type === 'error' ? (
                    <AlertTriangle className="w-5 h-5 text-[#FF2E2E]" />
                  ) : alertState.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-[#00FF94]" />
                  ) : (
                    <Info className="w-5 h-5 text-[#00F0FF]" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold mb-2">
                    {alertState.type === 'error' ? 'Error' :
                     alertState.type === 'success' ? 'Success' :
                     'Notice'}
                  </h3>
                  <p className="text-white/70 text-sm">{alertState.message}</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={() => setAlertState({ ...alertState, isOpen: false })}
                  className="btn-primary"
                >
                  OK
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return { showAlert, AlertDialog };
};

// Custom Confirm Dialog
export const useConfirm = () => {
  const [confirmState, setConfirmState] = useState({ 
    isOpen: false, 
    message: '', 
    title: 'Confirm',
    onConfirm: null 
  });

  const showConfirm = (message, title = 'Confirm') => {
    return new Promise((resolve) => {
      setConfirmState({ 
        isOpen: true, 
        message, 
        title,
        onConfirm: resolve 
      });
    });
  };

  const handleConfirm = (result) => {
    if (confirmState.onConfirm) {
      confirmState.onConfirm(result);
    }
    setConfirmState({ ...confirmState, isOpen: false });
  };

  const ConfirmDialog = () => (
    <AnimatePresence>
      {confirmState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => handleConfirm(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md"
          >
            <div 
              className="glass-card rounded-xl p-6"
              style={{
                background: 'rgba(10, 10, 11, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#FFB800]/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-[#FFB800]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold mb-2">{confirmState.title}</h3>
                  <p className="text-white/70 text-sm">{confirmState.message}</p>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <Button
                  onClick={() => handleConfirm(false)}
                  variant="outline"
                  className="border-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleConfirm(true)}
                  className="btn-primary"
                >
                  Confirm
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return { showConfirm, ConfirmDialog };
};

// Custom Prompt Dialog
export const usePrompt = () => {
  const [promptState, setPromptState] = useState({ 
    isOpen: false, 
    message: '', 
    title: 'Input',
    defaultValue: '',
    onSubmit: null 
  });

  const showPrompt = (message, title = 'Input', defaultValue = '') => {
    return new Promise((resolve) => {
      setPromptState({ 
        isOpen: true, 
        message, 
        title,
        defaultValue,
        onSubmit: resolve 
      });
    });
  };

  const handleSubmit = (value) => {
    if (promptState.onSubmit) {
      promptState.onSubmit(value);
    }
    setPromptState({ isOpen: false, message: '', title: 'Input', defaultValue: '', onSubmit: null });
  };

  const PromptDialog = () => (
    <AnimatePresence>
      {promptState.isOpen && (
        <PromptDialogContent
          promptState={promptState}
          handleSubmit={handleSubmit}
        />
      )}
    </AnimatePresence>
  );

  return { showPrompt, PromptDialog };
};

// Separate component to isolate re-renders
const PromptDialogContent = ({ promptState, handleSubmit }) => {
  const [localValue, setLocalValue] = useState(promptState.defaultValue);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => handleSubmit(null)}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-md"
      >
        <div 
          className="glass-card rounded-xl p-6"
          style={{
            background: 'rgba(10, 10, 11, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h3 className="font-heading font-semibold mb-2">{promptState.title}</h3>
          <p className="text-white/70 text-sm mb-4">{promptState.message}</p>
          
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="input-dark mb-4"
            placeholder=""
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit(localValue);
              if (e.key === 'Escape') handleSubmit(null);
            }}
          />
          
          <div className="flex gap-3 justify-end">
            <Button
              onClick={() => handleSubmit(null)}
              variant="outline"
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit(localValue)}
              className="btn-primary"
            >
              OK
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
